/** @file Timeline panel: toolbar, view tabs, the transport and the `timeline` key context. */
import type { IDockviewPanelProps } from 'dockview-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { editorDeps } from '../../playback/editorDeps';
import { playhead } from '../../playback/playhead';
import { errorText } from '../../shell/i18n/errorText';
import { useT } from '../../shell/i18n/useT';
import { registerEffective } from '../../shell/shortcuts/registerEffective';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';
import { Tabs } from '../../ui-kit/Tabs';
import { useFocusContext } from '../../ui-kit/useFocusContext';
import { bindings } from './bindings';
import { openAnimation } from './openAnimation';
import { timelineActions } from './timelineActions';
import { TimelineToolbar } from './TimelineToolbar';
import { TIMELINE_VIEWS } from './timelineViews';
import './timeline.css';

/** Mounted once (renderer 'always'); the clock runs in EditorFrame (startPlayback), and closing the panel pauses. */
export function TimelinePanel({ api }: IDockviewPanelProps) {
  const t = useT();
  const root = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const deps = useMemo(() => editorDeps((thrown) => { setFailure({ thrown }); }), []);
  const actions = useMemo(() => {
    const all = timelineActions(deps);
    // Key and Paste write into the open animation: nothing while it is missing (deleted by undo, MCP or another client).
    const whenOpen = (run: () => void) => (): void => { if (openAnimation() !== null) run(); };
    return { ...all, keyBones: whenOpen(all.keyBones), pasteKeys: whenOpen(all.pasteKeys) };
  }, [deps]);
  const [view, setView] = useState(TIMELINE_VIEWS[0]?.id ?? '');
  const picked = useStore(playheadStore, (s) => s.animation);
  const animation = useStore(docStore, (s) => (picked !== null && currentDoc(s)?.animations[picked] !== undefined ? picked : null));
  useFocusContext(root, 'timeline');
  useEffect(() => registerEffective('timeline', bindings, Object.fromEntries(Object.entries(actions).map(([name, run]) => [`timeline.${name}`, run]))), [actions]);
  // Space and the transport live here: once the panel closes nothing could pause a running playback.
  useEffect(() => () => { if (playheadStore.getState().playing) playhead.pause(); }, []);
  const Active = TIMELINE_VIEWS.find((v) => v.id === view)?.View;
  return (
    <div ref={root} className="timeline-panel" data-testid={`panel-${api.id}`}>
      <TimelineToolbar actions={actions} canKey={animation !== null} t={t} />
      {failure === null ? null : <p role="alert" className="timeline-error">{errorText(t, failure.thrown)}</p>}
      <Tabs tabs={TIMELINE_VIEWS.map((v) => ({ id: v.id, label: t(v.title) }))} activeId={view} onChange={setView} label={t('panels.timeline.views')} />
      {animation === null ? <p className="timeline-empty">{t('panels.timeline.noAnimation')}</p> : Active === undefined ? null : <Active deps={deps} />}
    </div>
  );
}
